import { formatQty, restockSendInput, type RestockSendInput } from '@servwell/shared';
import { sameAccount, type AccountRef } from './account';
import { isRetryableMutationRefusal, refusalStatus } from './refusal';
import {
  legacyRestockSendKey,
  parseLegacyRestockSend,
  withOfficeInventoryLock,
  hasBlockingReorderWrite,
} from './reorder-write-recovery';
function asQuantity(value: number) {
  return value % 100 === 0
    ? String(value / 100)
    : `${Math.floor(value / 100)}.${String(value % 100).padStart(2, '0')}`;
}
export type RestockSendAttempt = Omit<RestockSendInput, 'acknowledgeNegative'> & {
  acknowledgeNegative?: boolean;
  accepted?: boolean;
};

export function restockSendAttemptForAccount(
  attempt: RestockSendAttempt | null,
  attemptOwner: AccountRef | null,
  account: AccountRef,
) {
  return attempt && attemptOwner && sameAccount(attemptOwner, account) ? attempt : null;
}

export function restockSendRecoveryAcceptedForAccount(
  attempt: RestockSendAttempt | null,
  attemptOwner: AccountRef | null,
  sendConfirmed: boolean,
  confirmedOwner: AccountRef | null,
  recoveryOwner: AccountRef | null,
) {
  return Boolean(
    (attempt?.accepted &&
      attemptOwner &&
      recoveryOwner &&
      sameAccount(attemptOwner, recoveryOwner)) ||
      (sendConfirmed &&
        confirmedOwner &&
        recoveryOwner &&
        sameAccount(confirmedOwner, recoveryOwner)),
  );
}

export function restockSendErrorForAccount<ErrorType>(
  error: ErrorType | null,
  errorOwner: AccountRef | null,
  account: AccountRef,
) {
  return sameAccount(errorOwner, account) ? error : null;
}

export function restockSendRetryEnabled(
  attemptOwner: AccountRef | null,
  account: AccountRef,
  pending: boolean,
) {
  return Boolean(attemptOwner && sameAccount(attemptOwner, account) && !pending);
}

export function restockSendStorageErrorMessage(accepted: boolean) {
  return accepted
    ? 'The Send was accepted, but this device could not save or clear its recovery receipt. Retry the same key or clear the accepted receipt.'
    : 'This device could not clear its saved Send. Retry the same key, or verify that no restock was created before discarding it.';
}

export function classifyRestockSendFailure(
  error: unknown,
  variables: RestockSendAttempt,
): { kind: 'review' } | { kind: 'retry'; variables: RestockSendAttempt } {
  // Authorization runs before Send's idempotency replay; a 403 cannot prove that an
  // earlier delivery with this key was not already accepted.
  if (refusalStatus(error) === 403) return { kind: 'retry', variables };
  return isRetryableMutationRefusal(error)
    ? { kind: 'retry', variables }
    : { kind: 'review' };
}

export function loadRestockSendAttempt(
  owner: AccountRef,
  vanId: string,
): RestockSendAttempt | null {
  try {
    const raw = localStorage.getItem(legacyRestockSendKey(owner, vanId));
    if (!raw) return null;
    const stored = parseLegacyRestockSend(raw);
    return stored && stored.attempt.vanId === vanId
      ? { ...stored.attempt, ...(stored.accepted ? { accepted: true } : {}) }
      : null;
  } catch {
    return null;
  }
}

export function hasUnreadableRestockSendAttempt(owner: AccountRef, vanId: string) {
  let raw: string | null;
  try {
    raw = localStorage.getItem(legacyRestockSendKey(owner, vanId));
  } catch {
    return false;
  }
  if (raw === null) return false;
  try {
    const stored = parseLegacyRestockSend(raw);
    return !stored || stored.attempt.vanId !== vanId;
  } catch {
    return true;
  }
}

export async function saveRestockSendAttempt(
  owner: AccountRef,
  vanId: string,
  variables: RestockSendAttempt,
): Promise<boolean> {
  try {
    return await withOfficeInventoryLock(owner, () => {
      if (hasBlockingReorderWrite(owner, vanId)) return false;

      try {
        if (variables.vanId !== vanId || !restockSendInput.safeParse(variables).success)
          return false;
        const key = legacyRestockSendKey(owner, vanId);
        const candidate = restockSendInput.parse(variables);
        const existingRaw = localStorage.getItem(key);
        if (existingRaw) {
          const existing = parseLegacyRestockSend(existingRaw);
          if (!existing || existing.attempt.mutationId !== candidate.mutationId)
            return false;
          if (JSON.stringify(existing.attempt) !== JSON.stringify(candidate))
            return false;
          if (existing.accepted) return true;
        }
        localStorage.setItem(key, JSON.stringify(candidate));
        return true;
      } catch {
        return false;
      }
    });
  } catch {
    return false;
  }
}

export async function markRestockSendAttemptAccepted(
  owner: AccountRef,
  vanId: string,
  mutationId: string,
): Promise<boolean> {
  try {
    return await withOfficeInventoryLock(owner, () => {
      try {
        const key = legacyRestockSendKey(owner, vanId);
        const raw = localStorage.getItem(key);
        if (!raw) return false;
        const stored = parseLegacyRestockSend(raw);
        if (
          !stored ||
          stored.attempt.vanId !== vanId ||
          stored.attempt.mutationId !== mutationId
        )
          return false;
        localStorage.setItem(key, JSON.stringify({ ...stored.attempt, accepted: true }));
        return loadRestockSendAttempt(owner, vanId)?.accepted === true;
      } catch {
        return false;
      }
    });
  } catch {
    return false;
  }
}

export async function restoreAcceptedRestockSendAttempt(
  owner: AccountRef,
  vanId: string,
  attempt: RestockSendAttempt,
) {
  try {
    return await withOfficeInventoryLock(owner, () => {
      if (hasBlockingReorderWrite(owner, vanId)) return false;

      try {
        if (attempt.vanId !== vanId) return false;
        const { accepted: _accepted, ...request } = attempt;
        const parsed = restockSendInput.safeParse(request);
        if (!parsed.success) return false;
        const key = legacyRestockSendKey(owner, vanId);
        const existingRaw = localStorage.getItem(key);
        if (existingRaw) {
          const existing = parseLegacyRestockSend(existingRaw);
          if (
            !existing ||
            existing.attempt.vanId !== vanId ||
            existing.attempt.mutationId !== parsed.data.mutationId ||
            JSON.stringify(existing.attempt) !== JSON.stringify(parsed.data)
          )
            return false;
          if (existing.accepted) return true;
        }
        localStorage.setItem(key, JSON.stringify({ ...parsed.data, accepted: true }));
        const restored = loadRestockSendAttempt(owner, vanId);
        return (
          restored?.mutationId === parsed.data.mutationId && restored.accepted === true
        );
      } catch {
        return false;
      }
    });
  } catch {
    return false;
  }
}

export async function clearRestockSendAttempt(
  owner: AccountRef,
  vanId: string,
  mutationId: string,
): Promise<boolean> {
  try {
    return await withOfficeInventoryLock(owner, () => {
      try {
        const key = legacyRestockSendKey(owner, vanId);
        const raw = localStorage.getItem(key);
        if (raw === null) return true;
        const stored = parseLegacyRestockSend(raw);
        if (!stored || stored.attempt.vanId !== vanId) return false;
        const current = stored.attempt;
        if (current.mutationId !== mutationId) return false;
        localStorage.removeItem(key);
        return localStorage.getItem(key) === null;
      } catch {
        // The unresolved key remains safer than forgetting an outcome the server may have saved.
        return false;
      }
    });
  } catch {
    return false;
  }
}

export async function clearUnreadableRestockSendAttempt(
  owner: AccountRef,
  vanId: string,
) {
  try {
    return await withOfficeInventoryLock(owner, () => {
      const key = legacyRestockSendKey(owner, vanId);
      let raw: string | null;
      try {
        raw = localStorage.getItem(key);
      } catch {
        return false;
      }
      if (raw === null) return true;
      try {
        const stored = parseLegacyRestockSend(raw);
        if (stored && stored.attempt.vanId === vanId) return false;
      } catch {
        // An unreadable value has no safe request key to retry.
      }
      try {
        localStorage.removeItem(key);
        return localStorage.getItem(key) === null;
      } catch {
        return false;
      }
    });
  } catch {
    return false;
  }
}

export function reconcileRestockSendAttemptAfterClearFailure(
  latestStored: RestockSendAttempt,
  currentAttempt: RestockSendAttempt | null,
  sendConfirmed: boolean,
) {
  const different = currentAttempt?.mutationId !== latestStored.mutationId;
  const accepted =
    latestStored.accepted === true ||
    (!different && (currentAttempt?.accepted === true || sendConfirmed));
  return {
    attempt: { ...latestStored, ...(accepted ? { accepted: true } : {}) },
    accepted,
    different,
  };
}

export function restockAttemptQuantity(
  attempt: RestockSendAttempt | null,
  itemId: string,
): string | null {
  const item = attempt?.items.find((entry) => entry.itemId === itemId);
  return item ? asQuantity(item.sendHundredths) : null;
}
